import React, { useEffect, useState, useRef } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useParams } from 'react-router-dom';
import { useApi } from '../lib/useApi';
import { useAuthFetch } from '../lib/useAuthFetch';
import { useAppUser } from '../context/AppUserContext';
import SharedHeaderContractor from './SharedHeaderContractor';
import { getApiUrl } from '../lib/getApiUrl';
// CB:BEGIN demo-bids-shared-import
import { getSelectedDemoProject, getDemoProjects } from '../lib/demoBids';
// CB:END demo-bids-shared-import
// CB:BEGIN stripe-elements-import
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import stripePromise from '../lib/stripe';
// CB:END stripe-elements-import
// CB:BEGIN p0-contractor-flow
import { P0ProjectFlow } from './P0ProjectFlow';
// CB:END p0-contractor-flow
// CB:BEGIN contractor-quote-bid-form-import
import ContractorQuoteBidForm from './ContractorQuoteBidForm';
// CB:END contractor-quote-bid-form-import
// CB:BEGIN contractor-escrow-section-import
import ContractorEscrowSection from './ContractorEscrowSection';
// CB:END contractor-escrow-section-import
// CB:BEGIN contractor-inline-help-import
import ContractorInlineHelpShortcut from './help/ContractorInlineHelpShortcut';
// CB:END contractor-inline-help-import

// Check Stripe key availability on module load (silently - no logging of keys)
if (typeof window !== 'undefined') {
  const key = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
  if (!key) {
    // Key is fetched from backend API at runtime (/api/config/stripe) — not required in build env
    console.debug('[Stripe] VITE_STRIPE_PUBLISHABLE_KEY not in build env — will be fetched from API');
  } else if (!key.startsWith('pk_')) {
    console.error('❌ VITE_STRIPE_PUBLISHABLE_KEY appears invalid - should start with pk_');
  }
  // Removed key logging for security - key is validated but not logged
}

interface ProjectDetailsProps {
  onNavigate: (screen: string) => void;
}

// CB:BEGIN fix-tdz-date-helpers
// FIX: Use stdlib dates to avoid extra deps - define after interfaces to avoid TDZ
const toDateStr = (d: Date) => d.toISOString().slice(0, 10);
const addDays = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
// CB:END fix-tdz-date-helpers

const ProjectDetails: React.FC<ProjectDetailsProps> = ({ onNavigate }) => {
  const { user } = useUser();
  const api = useApi();
  const authFetch = useAuthFetch();
  const { contractorsSelf } = useAppUser();
  const { saveBidDraft, getBidDraft, getContractorPayoutStatus, listContractorTemplates, getContractorTemplate, createContractorTemplate, deleteContractorTemplate } = api;
  // CB:BEGIN stable-onNavigate-ref - Avoid effect re-runs when parent passes new onNavigate reference (stops quote page reload loop).
  const onNavigateRef = useRef(onNavigate);
  onNavigateRef.current = onNavigate;
  // CB:END stable-onNavigate-ref
  // CB:BEGIN payout-readiness-state-project-details
  const [payoutStatus, setPayoutStatus] = useState<{
    payoutReady: boolean;
    payoutReadyAt: string | null;
    stripeAccountId: string | null;
  } | null>(null);
  const [loadingPayout, setLoadingPayout] = useState(true);
  // CB:END payout-readiness-state-project-details
  // CB:BEGIN platform-fee-info-modal
  const [showPlatformFeeInfo, setShowPlatformFeeInfo] = useState(false);
  // CB:END platform-fee-info-modal
  // CB:BEGIN contractor-templates-state
  const [contractorTemplates, setContractorTemplates] = useState<Array<{ id: string; name: string; updatedAt: string }>>([]);
  // CB:END contractor-templates-state
  
  // FIX: Read projectId from URL accepting any token (cuid, 'demo-project', numeric)
  // FIX: Prioritize URL path over localStorage (URL is source of truth)
  const path = window.location.pathname;
  const urlParams = new URLSearchParams(window.location.search);
  const queryId = urlParams.get("projectId") || undefined;
  // capture everything up to query/hash
  const m = path.match(/\/contractor\/projects\/([^/?#]+)/);
  const paramId = m ? m[1] : undefined;
  // Only use localStorage as fallback if URL doesn't have project ID
  const stateId = paramId ? undefined : (localStorage.getItem('active_project_id') || undefined);
  const projectId = paramId || stateId || queryId;
  
  // CB:BEGIN improved-project-id-extraction
  // Log the project ID extraction for debugging
  React.useEffect(() => {
    console.log('🔍 ProjectDetails: Extracted projectId:', { path, paramId, stateId, queryId, final: projectId });
    console.log('🔍 ProjectDetails: Component mounted/updated, current projectId:', projectId);
  }, [path, paramId, stateId, queryId, projectId]);
  // CB:END improved-project-id-extraction
  
  // FIX: Guard when no project id is present to avoid blank screen
  React.useEffect(() => {
    if (!projectId || projectId === 'undefined' || projectId === 'null') {
      console.warn("ProjectDetails: no valid projectId, sending back to current-bids");
      onNavigateRef.current("/contractor/current-bids");
      return;
    }
  }, [projectId]);
  
  // CB:BEGIN fetch-real-project-data
  const [project, setProject] = useState<any>(null);
  const [loading, setLoading] = useState(true); // CB:BEGIN loading-state - Track loading state
  const [error, setError] = useState<string | null>(null); // CB:BEGIN error-state - Track errors
  const [hasAcceptedBid, setHasAcceptedBid] = useState(false); // CB:BEGIN contractor-accepted-bid-check - Track if contractor has accepted bid
  // CB:BEGIN start-date-workflow
  const [startDateData, setStartDateData] = useState<any>(null);
  const [sdActionLoading, setSdActionLoading] = useState<string | null>(null);
  const [counterDateInput, setCounterDateInput] = useState('');
  const [showCounterInput, setShowCounterInput] = useState(false);
  // CB:END start-date-workflow
  // CB:BEGIN contractor-escrow-account-check
  const [hasEscrowAccount, setHasEscrowAccount] = useState(false);
  const [checkingEscrow, setCheckingEscrow] = useState(true);
  // CB:END contractor-escrow-account-check
  
  // Prefer the loaded project ID, fall back to route param (must be after project state is declared)
  const effectiveProjectId = project?.id ?? projectId;
  
  // Route param(s) – support /contractor/projects/:projectId or similar
  const routeParams = useParams<{ projectId?: string; id?: string }>();
  const routeProjectId = routeParams.projectId ?? routeParams.id ?? null;
  
  // Prefer the loaded project's ID, fall back to the route param
  const currentProjectId = project?.id ?? routeProjectId ?? projectId ?? null;
  
  console.log('🔍 ProjectDetails: currentProjectId resolved:', {
    projectIdFromRoute: routeProjectId,
    projectIdFromState: project?.id ?? null,
    effectiveProjectId,
    currentProjectId,
  });
  
  // CB:BEGIN fetch-payout-status-project-details
  const getContractorPayoutStatusRef = useRef(getContractorPayoutStatus);
  getContractorPayoutStatusRef.current = getContractorPayoutStatus;
  useEffect(() => {
    if (!user?.id) {
      setLoadingPayout(false);
      return;
    }

    let isMounted = true;
    (async () => {
      try {
        const status = await getContractorPayoutStatusRef.current();
        if (isMounted) {
          setPayoutStatus(status);
        }
      } catch (error) {
        console.error('Error fetching payout status:', error);
        if (isMounted) {
          setPayoutStatus(null);
        }
      } finally {
        if (isMounted) {
          setLoadingPayout(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [user?.id]);
  // CB:END fetch-payout-status-project-details

  // CB:BEGIN fetch-contractor-info-for-quote
  // Use already-authenticated context data (AppUserContext fetches /api/contractors/self with Bearer token at app load)
  useEffect(() => {
    const c = contractorsSelf?.contractor;
    if (c) {
      setContractorInfo({
        company: c.company ?? null,
        license: c.licenseNumber ?? null,
        insurance: c.insurance ?? null,
        phone: c.phone ?? null,
      });
    }
  }, [contractorsSelf]);
  // CB:END fetch-contractor-info-for-quote

  useEffect(() => {
    const fetchProject = async () => {
      // CB:BEGIN skip-demo-and-invalid-ids
      // Skip fetching if projectId is undefined, "0", or "demo-project"
      if (!projectId || projectId === '0' || projectId === 'demo-project') {
        console.warn('⚠️ Invalid project ID:', projectId);
        setLoading(false);
        setError('Invalid project ID');
        onNavigateRef.current('/contractor/current-bids');
        return;
      }
      // CB:END skip-demo-and-invalid-ids
      
      // Prevent duplicate fetches if already loading or project already matches
      if (loading && project?.id === projectId) {
        return;
      }
      
      setLoading(true);
      setError(null);
      
      try {
        // CB:BEGIN fix-api-url-project-details
        // Use api.get() so Clerk Bearer token is sent (avoids 403 for contractor project:read)
        console.log('🔍 ProjectDetails: Fetching project from: /api/projects/' + projectId);
        const data = await api.get<any>(`/api/projects/${projectId}`, { dedupeKey: false });
        // CB:END fix-api-url-project-details
        if (data) {
          console.log('✅ Fetched real project:', data);
          // CB:BEGIN validate-project-data
          // Backend returns project directly, or { project: ... }, handle both
          const projectData = data?.project || data;
          if (projectData && projectData.id) {
            setProject(projectData);
            console.log('✅ Project data set successfully:', { id: projectData.id, address: projectData.address });
            setError(null);
            
            // CB:BEGIN contractor-accepted-bid-check
            // Check if current contractor has an accepted bid on this project
            const userId = user?.id;
            const bids = projectData.bids || [];
            
            // Find accepted bid
            const acceptedBid = bids.find((bid: any) => bid.isAccepted === true);
            
            // Check if the accepted bid belongs to the current contractor
            let contractorHasAcceptedBid = false;
            if (acceptedBid && userId) {
              // Check via contractor.user.clerkId
              if (acceptedBid.contractor?.user?.clerkId === userId) {
                contractorHasAcceptedBid = true;
              }
              // Also check if we can find the contractor by userId from the database
              // The bids might not have full contractor relationship loaded
              if (!contractorHasAcceptedBid && acceptedBid.contractorId) {
                // Try to match by checking if current user's contractorId matches
                try {
                  const { getApiUrl } = await import('../lib/getApiUrl');
                  const contractorRes = await authFetch(getApiUrl(`/api/contractors/self?userId=${encodeURIComponent(userId)}`));
                  if (contractorRes.ok) {
                    const contractorData = await contractorRes.json();
                    if (contractorData?.contractor?.id === acceptedBid.contractorId) {
                      contractorHasAcceptedBid = true;
                    }
                  }
                } catch (err) {
                  console.warn('Could not verify contractor match:', err);
                }
              }
            }
            
            setHasAcceptedBid(contractorHasAcceptedBid);
            console.log('🔍 Contractor accepted bid check:', { 
              hasAcceptedBid: contractorHasAcceptedBid, 
              userId, 
              acceptedBid: acceptedBid ? { id: acceptedBid.id, contractorId: acceptedBid.contractorId } : null,
              allBids: bids.length 
            });
            
            // CB:BEGIN load-contractor-notes
            // Load contractor notes from localStorage if they have an accepted bid
            if (contractorHasAcceptedBid && projectData.id) {
              try {
                const savedNotes = localStorage.getItem(`contractor_notes_${projectData.id}`);
                if (savedNotes) {
                  setContractorNotes(savedNotes);
                }
              } catch (err) {
                console.warn('Could not load contractor notes:', err);
              }
            }
            // CB:END load-contractor-notes
            // CB:END contractor-accepted-bid-check

            // CB:BEGIN start-date-workflow-load
            if (contractorHasAcceptedBid && projectData.id) {
              try {
                const { getApiUrl } = await import('../lib/getApiUrl');
                const sdRes = await authFetch(getApiUrl(`/api/projects/${projectData.id}/start-date`));
                if (sdRes.ok) setStartDateData(await sdRes.json());
              } catch {}
            }
            // CB:END start-date-workflow-load

            // CB:BEGIN contractor-escrow-account-check
            // Check if escrow account exists for this project
            // Only check if contractor has accepted bid (escrow only exists after bid acceptance)
            if (projectData.id && contractorHasAcceptedBid) {
              try {
                const { getApiUrl } = await import('../lib/getApiUrl');
                const escrowResponse = await authFetch(getApiUrl(`/api/escrow/${projectData.id}/dashboard`));
                if (escrowResponse.ok) {
                  setHasEscrowAccount(true);
                } else if (escrowResponse.status === 404) {
                  // 404 is expected when no escrow account exists yet - don't log as error
                  setHasEscrowAccount(false);
                } else {
                  setHasEscrowAccount(false);
                }
              } catch (err) {
                // Network errors - don't log as warning
                setHasEscrowAccount(false);
              } finally {
                setCheckingEscrow(false);
              }
            } else {
              // No accepted bid or no project ID - no escrow account
              setHasEscrowAccount(false);
              setCheckingEscrow(false);
            }
            // CB:END contractor-escrow-account-check
          } else {
            console.warn('⚠️ Invalid project data received - missing id:', projectData);
            setError('Invalid project data');
            // CB:BEGIN fix-navigation-on-error
            onNavigateRef.current('contractor-dashboard');
            // CB:END fix-navigation-on-error
          }
          // CB:END validate-project-data
        }
      } catch (error: any) {
        console.error('❌ Error fetching project:', error);
        const msg = error?.message ?? 'Failed to fetch project';
        const is403 = msg.includes('403') || msg.includes('Forbidden');
        const is404 = msg.includes('Not found') || msg.includes('404');
        setError(is403 ? `Failed to load project: 403` : msg);
        // Clear stale active_project_id if it matches the failing project ID
        if (is403 || is404 || msg === 'UNAUTHORIZED') {
          try {
            const storedId = localStorage.getItem('active_project_id');
            if (storedId === projectId) {
              localStorage.removeItem('active_project_id');
              console.warn('🗑️ Cleared stale active_project_id from localStorage:', storedId);
            }
          } catch {}
          onNavigateRef.current('contractor-dashboard');
        }
      } finally {
        setLoading(false); // CB:BEGIN loading-state - Always set loading to false
      }
    };
    
    fetchProject();
  }, [projectId, user?.id]);
  
  // CB:BEGIN contractor-escrow-event-listener
  // Listen for escrow account creation events
  useEffect(() => {
    if (!projectId) return;
    
    const handleEscrowCreated = () => {
      const checkEscrow = async () => {
        try {
          const { getApiUrl } = await import('../lib/getApiUrl');
          const escrowResponse = await authFetch(getApiUrl(`/api/escrow/${projectId}/dashboard`));
          if (escrowResponse.ok) {
            setHasEscrowAccount(true);
          } else if (escrowResponse.status === 404) {
            // 404 is expected - don't log
            setHasEscrowAccount(false);
          }
        } catch (err) {
          // Network errors - don't log
          setHasEscrowAccount(false);
        }
      };
      checkEscrow();
    };
    
    window.addEventListener('cb:escrow-created', handleEscrowCreated);
    window.addEventListener('cb:payment-succeeded', handleEscrowCreated);
    
    return () => {
      window.removeEventListener('cb:escrow-created', handleEscrowCreated);
      window.removeEventListener('cb:payment-succeeded', handleEscrowCreated);
    };
  }, [projectId]);
  // CB:END contractor-escrow-event-listener
  
  // CB:END fetch-real-project-data
  // FIX: Start with null so field is empty; no hardcoded defaults like 18500
  const [amount, setAmount] = useState<number | null>(null);
  const [notes, setNotes] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem('contractor_demo_state') || 'null');
      if (s && typeof s.bidNotes === 'string') return s.bidNotes;
    } catch {}
    return '';
  });
  // CB:BEGIN bid-extra-fields
  const [materialType, setMaterialType] = useState<string>('');
  const [materialWarrantyYears, setMaterialWarrantyYears] = useState<number | null>(null);
  const [workmanshipWarrantyYears, setWorkmanshipWarrantyYears] = useState<number | null>(null);
  // CB:END bid-extra-fields
  // CB:BEGIN bid-submitted-modal-state
  const [showSubmittedModal, setShowSubmittedModal] = useState(false);
  // CB:END bid-submitted-modal-state
  // CB:BEGIN contractor-project-notes-state
  const [contractorNotes, setContractorNotes] = useState<string>('');
  const [savingNotes, setSavingNotes] = useState(false);
  // CB:END contractor-project-notes-state
  
  // FIX: add date state and auto-calc completion date
  const now = new Date();
  const [startDate, setStartDate] = useState<string>(toDateStr(now));
  const [completionDate, setCompletionDate] = useState<string>(toDateStr(addDays(now, 5)));
  const [submitting, setSubmitting] = useState(false);
  // CB:BEGIN bid-fee-and-payment-modals
  const [showPaymentRequired, setShowPaymentRequired] = useState(false);
  const [showVerificationRequired, setShowVerificationRequired] = useState(false);
  const [showFeeConfirm, setShowFeeConfirm] = useState(false);
  const pendingBidRef = useRef<{ start: string; end: string; amount: number; paymentIntentId?: string; note?: string; isFirstBid?: boolean } | null>(null);
  // CB:END bid-fee-and-payment-modals
  // CB:BEGIN contractor-quote-bid-draft-state
  const [bidDraft, setBidDraft] = useState<{ amountCents?: number | null; note?: string | null; formData?: Record<string, unknown> | null; updatedAt?: string | null } | null>(null);
  const [contractorInfo, setContractorInfo] = useState<{ company?: string | null; license?: string | null; insurance?: string | null; phone?: string | null } | null>(null);
  // CB:END contractor-quote-bid-draft-state

  React.useEffect(() => {
    const d = new Date(startDate);
    if (!isNaN(d.getTime())) setCompletionDate(toDateStr(addDays(d, 5)));
  }, [startDate]);

  // CB:BEGIN fetch-questionnaire-answers
  // Build questionnaire answers from project data
  const buildQAnswers = (q: any) => {
    if (!q) return [];
    
    const questions = [
      { key: 'question1', answerKey: 'answer1', label: 'Q1 · Start Timeline' },
      { key: 'question2', answerKey: 'answer2', label: 'Q2 · Property Type' },
      { key: 'question3', answerKey: 'answer3', label: 'Q3 · Work Needed' },
      { key: 'question4', answerKey: 'answer4', label: 'Q4 · Reason for Work' },
      { key: 'question5', answerKey: 'answer5', label: 'Q5 · Preferred Roof Type' },
      { key: 'question7', answerKey: 'answer7', label: 'Q7 · Insurance Coverage' }
    ];
    
    return questions
      .filter(item => q[item.key] && q[item.answerKey]) // Only include questions that have both question and answer
      .map(item => ({
        label: q[item.key] || item.label,
        value: q[item.answerKey] || 'N/A'
      }));
  };
  
  const qAnswers = project?.questionnaire ? buildQAnswers(project.questionnaire) : [];
  // CB:END fetch-questionnaire-answers
  
  // Load existing draft on mount; if none, fall back to demo default once
  useEffect(() => {
    (async () => {
      try {
        const clerkId = user?.id as string;
        if (!clerkId || !currentProjectId) {
          // CB:BEGIN draft-prefill-local-fallback
          try {
            const s = JSON.parse(localStorage.getItem('contractor_demo_state') || '{}');
            const raw = Number(String(s?.bidAmountInput || '').replace(/[^0-9.]/g, '')) || undefined;
            if (typeof raw === 'number' && !Number.isNaN(raw) && raw > 0) setAmount(Math.round(raw));
            if (typeof s?.bidNotes === 'string' && s.bidNotes) setNotes(s.bidNotes);
          } catch {}
          // CB:END draft-prefill-local-fallback
          return;
        }
        const res: any = await getBidDraft(String(currentProjectId));
        const draft = res?.bid;
        // CB:BEGIN contractor-templates-load
        listContractorTemplates().then(setContractorTemplates).catch(() => {});
        // CB:END contractor-templates-load
        if (draft) {
          setBidDraft({ amountCents: draft.amountCents ?? null, note: draft.note ?? null, formData: draft.formData ?? null, updatedAt: draft.updatedAt ?? null });
          const newAmount = typeof draft.amountCents === 'number' ? Math.max(0, Math.round(draft.amountCents / 100)) : null;
          if (newAmount !== null) setAmount(newAmount);
          if (typeof draft.note === 'string') setNotes(draft.note);
        } else {
          setBidDraft(null);
          // If no DB draft, fall back to any prior local draft
          try {
            const s = JSON.parse(localStorage.getItem('contractor_demo_state') || '{}');
            const raw = Number(String(s?.bidAmountInput || '').replace(/[^0-9.]/g, '')) || undefined;
            if (typeof raw === 'number' && !Number.isNaN(raw) && raw > 0) {
              setAmount(Math.round(raw));
            }
            if (typeof s?.bidNotes === 'string' && s.bidNotes) setNotes(s.bidNotes);
          } catch {
            // Leave amount as null (empty)
          }
        }
      } catch {
        // No draft -> leave empty so card says Bid Now
        setAmount(null);
      }
    })();
  }, [user?.id]);

  const effectiveBid = (typeof amount === 'number' ? amount : 0);
  const platformFee = Math.round(effectiveBid * 0.10);
  const totalAmount = effectiveBid + platformFee;

  // CB:BEGIN contractor-quote-submit-handlers
  const handleQuoteSubmitBid = async (payload: { amountCents: number; earliestStartDate: string; estimatedCompletionDate: string; note: string }) => {
    if (submitting) return;
    if (project?.status === 'CLOSED') { alert('Project is closed to new bids.'); return; }
    const clerkId = user?.id as string;
    if (!clerkId) { alert('Please sign in to submit a bid.'); return; }
    try {
      const { getApiUrl } = await import('../lib/getApiUrl');
      let isFirstBid = false;
      try {
        const selfRes = await authFetch(getApiUrl(`/api/contractors/self?userId=${encodeURIComponent(clerkId)}`));
        if (selfRes.ok) {
          const selfData = await selfRes.json();
          const bidCount = selfData?.contractor?.bidCount ?? 1;
          isFirstBid = bidCount === 0;
        }
      } catch (_) {}
      if (isFirstBid) {
        pendingBidRef.current = { start: payload.earliestStartDate, end: payload.estimatedCompletionDate, amount: payload.amountCents / 100, note: payload.note, isFirstBid: true };
      } else {
        try {
          const vRes = await authFetch(getApiUrl(`/api/contractor-verification/${encodeURIComponent(clerkId)}`));
          if (vRes.ok) {
            const v = await vRes.json();
            if (!v?.verified) {
              pendingBidRef.current = { start: payload.earliestStartDate, end: payload.estimatedCompletionDate, amount: payload.amountCents / 100, note: payload.note };
              setShowVerificationRequired(true);
              return;
            }
          }
        } catch {}
        if (!pendingBidRef.current) {
          pendingBidRef.current = { start: payload.earliestStartDate, end: payload.estimatedCompletionDate, amount: payload.amountCents / 100, note: payload.note };
        }
        if (currentProjectId) (pendingBidRef.current as any).projectId = currentProjectId;
        try {
          const autoFeeRes = await authFetch(getApiUrl('/api/payments/contractor-bid-fee'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ clerkId, projectId: currentProjectId || projectId }),
          });
          const autoFeeData = await autoFeeRes.json();
          if (autoFeeRes.ok && autoFeeData.charged && autoFeeData.status === 'succeeded') {
            pendingBidRef.current = { start: payload.earliestStartDate, end: payload.estimatedCompletionDate, amount: payload.amountCents / 100, note: payload.note, paymentIntentId: autoFeeData.paymentIntentId };
          } else if (autoFeeData.requiresPaymentMethod || autoFeeData.error === 'payment_method_required' || autoFeeData.status === 'requires_payment_method' || (autoFeeRes.ok && !autoFeeData.charged)) {
            pendingBidRef.current = { start: payload.earliestStartDate, end: payload.estimatedCompletionDate, amount: payload.amountCents / 100, note: payload.note, existingPaymentIntentId: autoFeeData.paymentIntentId };
            setShowFeeConfirm(true);
            return;
          } else {
            pendingBidRef.current = { start: payload.earliestStartDate, end: payload.estimatedCompletionDate, amount: payload.amountCents / 100, note: payload.note };
            setShowFeeConfirm(true);
            return;
          }
        } catch {
          pendingBidRef.current = { start: payload.earliestStartDate, end: payload.estimatedCompletionDate, amount: payload.amountCents / 100, note: payload.note };
          setShowFeeConfirm(true);
          return;
        }
      }
      if (!pendingBidRef.current?.isFirstBid && !pendingBidRef.current?.paymentIntentId && !showFeeConfirm) {
        alert('Payment confirmation required before submitting bid');
        return;
      }
      setSubmitting(true);
      try {
        const paymentIntentId = pendingBidRef.current?.isFirstBid ? undefined : pendingBidRef.current?.paymentIntentId;
        const resp = await authFetch(getApiUrl('/api/bids/submit'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            projectId: currentProjectId || projectId,
            clerkId,
            amountCents: payload.amountCents,
            paymentIntentId,
            earliestStartDate: payload.earliestStartDate,
            estimatedCompletionDate: payload.estimatedCompletionDate,
            note: (pendingBidRef.current as any)?.note ?? payload.note,
          }),
        });
        if (!resp.ok) {
          let message = await resp.text();
          let code: string | undefined;
          try {
            const jsonError = JSON.parse(message);
            message = jsonError.message || jsonError.error || message;
            code = jsonError.code;
          } catch {}
          if (resp.status === 403 && code === 'AGREEMENT_REQUIRED') {
            const goToVerification = window.confirm(
              'You must accept the Contractor Platform Agreement before submitting bids. Complete verification (step 5) to accept it. Go to verification now?'
            );
            if (goToVerification) onNavigate('/contractor-verification');
            throw new Error('Contractor Platform Agreement must be accepted before bidding.');
          }
          throw new Error(message || `Bid submit failed (${resp.status})`);
        }
        try {
          const id = String(projectId || localStorage.getItem('active_project_id') || 'demo-project');
          localStorage.setItem('active_project_id', id);
          const list = JSON.parse(localStorage.getItem('contractor_bidded_ids') || '[]');
          if (!Array.isArray(list) || !list.includes(id)) {
            localStorage.setItem('contractor_bidded_ids', JSON.stringify([...(Array.isArray(list) ? list : []), id]));
          }
          const state = JSON.parse(localStorage.getItem('contractor_demo_state') || '{}');
          state.bidSubmitted = true;
          state.hasActiveProject = true;
          state.bidAmountInput = (payload.amountCents / 100).toLocaleString(undefined, { style: 'currency', currency: 'USD' });
          localStorage.setItem('contractor_demo_state', JSON.stringify(state));
        } catch {}
        try {
          if (currentProjectId) {
            window.dispatchEvent(new CustomEvent('cb:bid-submitted', { detail: { projectId: currentProjectId, amount: payload.amountCents / 100 } }));
          }
        } catch {}
        alert('Bid submitted!');
        onNavigate('contractor-dashboard');
      } catch (e: any) {
        alert(e?.message ?? 'Bid submit failed');
      } finally {
        setSubmitting(false);
      }
    } catch {
      alert('Unable to verify payment setup. Please try again.');
    }
  };

  const handleQuoteSaveDraft = async (payload: { amountCents?: number; note?: string; formData?: Record<string, unknown> }) => {
    const clerkId = user?.id as string;
    if (!clerkId) throw new Error('no_clerk');
    const pid = currentProjectId || projectId;
    const result: any = await saveBidDraft(pid, { projectId: pid, amountCents: payload.amountCents, clerkId, note: payload.note, formData: payload.formData });
    try {
      const state = JSON.parse(localStorage.getItem('contractor_demo_state') || '{}');
      state.bidDraftSaved = true;
      state.bidAmountInput = payload.amountCents != null ? String(payload.amountCents / 100) : '';
      state.bidNotes = payload.note || '';
      localStorage.setItem('contractor_demo_state', JSON.stringify(state));
    } catch {}
    return { savedAt: result?.savedAt };
  };
  // CB:END contractor-quote-submit-handlers

  // CB:BEGIN start-date-workflow-handlers
  const refreshStartDateData = async () => {
    const pid = currentProjectId || projectId;
    if (!pid) return;
    try {
      const { getApiUrl } = await import('../lib/getApiUrl');
      const res = await authFetch(getApiUrl(`/api/projects/${pid}/start-date`));
      if (res.ok) setStartDateData(await res.json());
    } catch {}
  };

  const handleAcceptStartDate = async () => {
    const pid = currentProjectId || projectId;
    if (!pid) return;
    try {
      setSdActionLoading('accept');
      const { getApiUrl } = await import('../lib/getApiUrl');
      const res = await authFetch(getApiUrl(`/api/projects/${pid}/start-date/accept`), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      if (!res.ok) { const e = await res.json().catch(() => ({})); alert(e.message || 'Failed to accept date'); return; }
      await refreshStartDateData();
    } catch { alert('Failed to accept date. Please try again.'); }
    finally { setSdActionLoading(null); }
  };

  const handleCounterStartDate = async () => {
    const pid = currentProjectId || projectId;
    if (!pid || !counterDateInput) return;
    try {
      setSdActionLoading('counter');
      const { getApiUrl } = await import('../lib/getApiUrl');
      const suggestedStartDate = counterDateInput.includes('T') ? counterDateInput : `${counterDateInput}T12:00:00.000Z`;
      const res = await authFetch(getApiUrl(`/api/projects/${pid}/start-date/counter`), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ suggestedStartDate }) });
      if (!res.ok) { const e = await res.json().catch(() => ({})); alert(e.message || 'Failed to submit counter date'); return; }
      setShowCounterInput(false);
      setCounterDateInput('');
      await refreshStartDateData();
    } catch { alert('Failed to submit date. Please try again.'); }
    finally { setSdActionLoading(null); }
  };
  // CB:END start-date-workflow-handlers

  // CB:BEGIN loading-and-error-display
  // Show loading state or error while fetching
  if (loading) {
    return (
      <div className="container">
        <SharedHeaderContractor onNavigate={onNavigate} />
        <div className="content" style={{ maxWidth: 1000, margin: '0 auto', padding: '40px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: '18px', color: '#6b7280' }}>Loading project details...</div>
        </div>
      </div>
    );
  }
  
  if (error || !project) {
    return (
      <div className="container">
        <SharedHeaderContractor onNavigate={onNavigate} />
        <div className="content" style={{ maxWidth: 1000, margin: '0 auto', padding: '40px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: '18px', color: '#dc2626', marginBottom: '16px' }}>
            {error || 'Project not found'}
          </div>
          <button className="btn btn-primary" onClick={() => onNavigate('current-bids')}>
            Back to Current Bids
          </button>
        </div>
      </div>
    );
  }
  // CB:END loading-and-error-display
  
  return (
    <div className="container">
      <SharedHeaderContractor onNavigate={onNavigate} />

      {/* Content */}
      <div className="content">
        {/* CB:BEGIN contractor-inline-help-project-details */}
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 24px' }}>
          <ContractorInlineHelpShortcut
            onNavigate={onNavigate}
            sectionId="reviewing-jobs"
            label="Need help reviewing this job or handling milestones?"
          />
        </div>
        {/* CB:END contractor-inline-help-project-details */}
        {/* CB:BEGIN bidding-closed-banner */}
        {/* Removed banner - contractors working on project don't need to see this */}
        {/* CB:END bidding-closed-banner */}
        {/* CB:BEGIN bid-submitted-modal */}
        {showSubmittedModal && (
          <div role="dialog" aria-modal="true" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
            <div className="card" style={{ background: 'white', borderRadius: 12, padding: 20, width: 'min(420px, 92vw)', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}>
              <h3 style={{ marginTop: 0 }}>Bid Submitted</h3>
              <p style={{ color: '#374151' }}>Your bid has been submitted successfully.</p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                <button className="btn btn-primary" onClick={() => { setShowSubmittedModal(false); onNavigate('contractor-dashboard'); }}>OK</button>
              </div>
            </div>
          </div>
        )}
        {/* CB:END bid-submitted-modal */}
        {/* CB:BEGIN wider-project-quote-cards – use more horizontal space, less scrolling */}
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 24px' }}>
          <div style={{ marginTop: 32, marginBottom: 32 }}>
            <div style={{ width: '100%' }}>
            <div>
                <div className="dashboard-card" style={{ marginBottom: 24 }}>
                <div style={{ marginBottom: 16 }}>
                  <h2 style={{ marginBottom: 4 }}>{project?.address || 'Project Details'}</h2>
                  {(project?.homeowner?.firstName || project?.homeowner?.lastName) && (
                    <div style={{ fontSize: 14, color: '#6b7280' }}>
                      Homeowner Name: <span style={{ color: '#111827', fontWeight: 600 }}>
                        {[project.homeowner.firstName, project.homeowner.lastName].filter(Boolean).join(' ')}
                      </span>
                    </div>
                  )}
                </div>
                {(project as any)?.computedDistanceMiles != null && (
                  <p style={{ fontSize: 13, color: '#2563eb', fontWeight: 600, margin: '0 0 12px 0' }}>
                    📍 {Math.max(0.1, (project as any).computedDistanceMiles).toFixed(1)} miles from you
                  </p>
                )}
                {/* CB:BEGIN dynamic-project-details */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
                  <div>
                    <p><strong>Project ID:</strong> {projectId || project?.id || '—'}</p>
                    {/* CB:BEGIN first-milestone-start-date */}
                    <p><strong>Start:</strong> {(() => {
                      // Prefer homeowner-set scheduled start date
                      if (project?.scheduledStartAt) {
                        return new Date(project.scheduledStartAt).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
                      }
                      const firstMilestone = project?.phases?.find((p: any) => p.number === 1);
                      if (firstMilestone?.submittedAt) return new Date(firstMilestone.submittedAt).toLocaleDateString();
                      if (firstMilestone?.contractorSubmittedAt) return new Date(firstMilestone.contractorSubmittedAt).toLocaleDateString();
                      return project?.desiredStart ? new Date(project.desiredStart).toLocaleDateString() : 'TBD';
                    })()}</p>
                    {project?.roofColor && (
                      <p><strong>Roof color:</strong> {project.roofColor}</p>
                    )}
                    {/* CB:END first-milestone-start-date */}
                  </div>
                  <div>
                    <p><strong>Property:</strong> {project?.projectType || 'ROOF'}</p>
                    {/* CB:BEGIN active-status-display */}
                    <p><strong>Status:</strong> {(() => {
                      const status = project?.status || 'REQUESTED';
                      if (status === 'COMPLETED') return 'Completed';
                      if (hasAcceptedBid) return 'Active';
                      if (status === 'CLOSED') return 'Active';
                      return status;
                    })()}</p>
                    {/* CB:END active-status-display */}
                  </div>
                </div>
                {/* CB:END dynamic-project-details */}

                {/* Report links always visible — no Show More */}
                {project?.reportUrl && (
                  <div style={{ marginTop: 8 }}>
                    <a
                      href={project.reportUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#2563eb', textDecoration: 'underline', fontWeight: 600, fontSize: 14 }}
                    >
                      View Aerial Report (PDF)
                    </a>
                  </div>
                )}
                {project?.roofReportUrl && (
                  <div style={{ marginTop: 10 }}>
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          const apiUrl = getApiUrl(`/api/projects/${projectId}/roof-report/download?format=json`);
                          const res = await authFetch(apiUrl);
                          if (!res.ok) { alert('Failed to load roof report'); return; }
                          const data = await res.json();
                          if (data.downloadUrl) { window.open(data.downloadUrl, '_blank', 'noopener,noreferrer'); }
                          else { alert('Report not available'); }
                        } catch {
                          alert('Failed to load roof report');
                        }
                      }}
                      style={{ fontSize: 12, padding: '4px 10px', border: '1px solid #d1d5db', borderRadius: 6, background: '#f9fafb', color: '#374151', cursor: 'pointer', fontWeight: 500 }}
                    >
                      📄 View Roof Report (PDF)
                    </button>
                  </div>
                )}
                </div>
              </div>
              
              {/* CB:BEGIN contractor-active-project-actions */}
              {/* P0: Contractor sign contract, upload evidence, view escrow/start date */}
              {/* CB:BEGIN start-date-workflow-widget */}
              {hasAcceptedBid && startDateData && (() => {
                const sdStatus = startDateData.startDateStatus ?? 'NO_START_DATE';
                const isConfirmed = sdStatus === 'CONFIRMED';
                const isProposed = sdStatus === 'HOMEOWNER_PROPOSED';
                const isCountered = sdStatus === 'CONTRACTOR_COUNTERED';
                const preferredDate = startDateData.homeownerPreferredStartDate;
                const counterDate = startDateData.contractorSuggestedStartDate;
                const agreedDate = startDateData.agreedStartDate;
                return (
                  <div style={{ background: 'white', border: `1px solid ${isConfirmed ? '#6ee7b7' : isProposed ? '#fbbf24' : '#e5e7eb'}`, borderRadius: 12, padding: 20, marginBottom: 20 }}>
                    <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 10 }}>
                      Start Date {isConfirmed ? '✓' : isProposed ? '— Action Required' : isCountered ? '— Awaiting Homeowner' : ''}
                    </div>

                    {/* CONFIRMED */}
                    {isConfirmed && (
                      <div style={{ color: '#065f46', fontSize: 14 }}>
                        Agreed start date: <strong>{agreedDate ? new Date(agreedDate).toLocaleDateString() : 'Confirmed'}</strong>
                      </div>
                    )}

                    {/* NO_START_DATE */}
                    {sdStatus === 'NO_START_DATE' && (
                      <div style={{ color: '#6b7280', fontSize: 14 }}>Homeowner has not yet proposed a start date.</div>
                    )}

                    {/* HOMEOWNER_PROPOSED — contractor must act */}
                    {isProposed && (
                      <div>
                        <div style={{ fontSize: 14, marginBottom: 12 }}>
                          Homeowner proposed: <strong>{preferredDate ? new Date(preferredDate).toLocaleDateString() : '—'}</strong>
                        </div>
                        {!showCounterInput ? (
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <button onClick={handleAcceptStartDate} disabled={!!sdActionLoading} style={{ background: '#10b981', color: 'white', border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                              {sdActionLoading === 'accept' ? 'Accepting…' : 'Accept This Date'}
                            </button>
                            <button onClick={() => setShowCounterInput(true)} style={{ background: 'white', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, padding: '7px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                              Suggest Different Date
                            </button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                            <input type="date" value={counterDateInput} onChange={(e) => setCounterDateInput(e.target.value)} style={{ padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }} />
                            <button onClick={handleCounterStartDate} disabled={!counterDateInput || !!sdActionLoading} style={{ background: '#f59e0b', color: 'white', border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: !counterDateInput ? 0.5 : 1 }}>
                              {sdActionLoading === 'counter' ? 'Sending…' : 'Submit Counter'}
                            </button>
                            <button onClick={() => { setShowCounterInput(false); setCounterDateInput(''); }} style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* CONTRACTOR_COUNTERED — waiting for homeowner */}
                    {isCountered && (
                      <div style={{ fontSize: 14, color: '#92400e' }}>
                        You suggested <strong>{counterDate ? new Date(counterDate).toLocaleDateString() : '—'}</strong> — waiting for homeowner to respond.
                      </div>
                    )}
                  </div>
                );
              })()}
              {/* CB:END start-date-workflow-widget */}
              {/* CB:BEGIN project-escrow-section */}
              {hasAcceptedBid && currentProjectId && (
                <ContractorEscrowSection projectId={currentProjectId} onProjectComplete={() => setProject(prev => prev ? { ...prev, status: 'COMPLETED' } : prev)} />
              )}
              {/* CB:END project-escrow-section */}

              {/* CB:END contractor-active-project-actions */}
              {!hasAcceptedBid && (
              <>
                {/* CB:BEGIN homeowner-questionnaire-section */}
                <div className="dashboard-card" style={{ marginBottom: 24 }}>
                  <h3 style={{ marginBottom: 12, fontWeight: 600 }}>Homeowner&apos;s questionnaire responses</h3>
                  {qAnswers.length > 0 ? (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      {qAnswers.map((q, idx) => (
                        <div key={idx} style={{ padding: '10px 12px', background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb' }}>
                          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>{q.label}</div>
                          <div style={{ fontSize: 14, fontWeight: 500, color: '#111827' }}>
                            {String(q.value) === 'emergency'
                              ? 'Emergency - As soon as possible'
                              : String(q.value).replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ color: '#6b7280', fontSize: 14, fontStyle: 'italic', padding: '12px 0' }}>
                      No questionnaire data available for this project.
                    </div>
                  )}
                </div>
                {/* CB:END homeowner-questionnaire-section */}
                {/* CB:BEGIN contractor-quote-bid-form-replace */}
                <ContractorQuoteBidForm
                  project={project}
                  projectId={currentProjectId || projectId}
                  contractorInfo={contractorInfo}
                  onSubmitBid={handleQuoteSubmitBid}
                  onSaveDraft={handleQuoteSaveDraft}
                  submitting={submitting}
                  disabled={project?.status === 'CLOSED'}
                  onShowPlatformFeeInfo={() => setShowPlatformFeeInfo(true)}
                  initialDraft={bidDraft}
                  templates={contractorTemplates}
                  onLoadTemplate={async (id) => getContractorTemplate(id)}
                  onSaveTemplate={async (name, data) => createContractorTemplate(name, data)}
                  onDeleteTemplate={async (id) => deleteContractorTemplate(id)}
                  onRefreshTemplates={async () => { const t = await listContractorTemplates(); setContractorTemplates(t); }}
                />
                {/* CB:END contractor-quote-bid-form-replace */}
                {/* CB:BEGIN platform-fee-info-modal */}
                {showPlatformFeeInfo && (
                  <div
                    style={{
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 1000
                    }}
                    onClick={() => setShowPlatformFeeInfo(false)}
                  >
                    <div
                      style={{
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        padding: '24px',
                        maxWidth: '500px',
                        width: '90%',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                        position: 'relative'
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        onClick={() => setShowPlatformFeeInfo(false)}
                        style={{
                          position: 'absolute',
                          top: '12px',
                          right: '12px',
                          background: 'none',
                          border: 'none',
                          fontSize: '24px',
                          cursor: 'pointer',
                          color: '#6b7280',
                          lineHeight: '1',
                          padding: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '4px',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f3f4f6';
                          e.currentTarget.style.color = '#1f2937';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = '#6b7280';
                        }}
                        aria-label="Close"
                      >
                        ×
                      </button>
                      <h3 style={{ marginTop: 0, marginBottom: '16px', color: '#1e40af', fontSize: '20px', fontWeight: '600' }}>
                        Taxes and Fees
                      </h3>
                      <p style={{ margin: 0, color: '#374151', lineHeight: '1.6', fontSize: '15px' }}>
                        The Taxes and Fees is added on top of your bid. If you win the project, you only keep 100% of your bid amount.
                      </p>
                      <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
                        <button
                          type="button"
                          onClick={() => setShowPlatformFeeInfo(false)}
                          style={{
                            backgroundColor: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '10px 20px',
                            fontSize: '14px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#2563eb'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#3b82f6'; }}
                        >
                          Got it
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                {/* CB:END platform-fee-info-modal */}
              </>
              )}
              {/* CB:END contractor-active-project-actions */}
            </div>
            </div>
          </div>
        </div>
        {/* CB:END wider-project-quote-cards */}

      {/* CB:BEGIN payment-required-modal */}
      {showPaymentRequired && (
        <div role="dialog" aria-modal="true" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }} onClick={() => setShowPaymentRequired(false)}>
          <div className="card" style={{ background: 'white', borderRadius: 12, padding: 20, width: 'min(420px, 92vw)', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Add Payment Information</h3>
            <p style={{ color: '#374151' }}>You need to add a default payment method before submitting a bid.</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button className="btn btn-secondary" onClick={() => setShowPaymentRequired(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={() => { setShowPaymentRequired(false); 
                // Navigate to payments/setup area; fallback to contractor dashboard
                onNavigate('/contractor-dashboard?tab=payments');
              }}>Add Payment Info</button>
      </div>
          </div>
        </div>
      )}
      {/* CB:END payment-required-modal */}

      {/* CB:BEGIN verification-required-modal */}
      {showVerificationRequired && (
        <div role="dialog" aria-modal="true" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }} onClick={() => setShowVerificationRequired(false)}>
          <div className="card" style={{ background: 'white', borderRadius: 12, padding: 20, width: 'min(460px, 92vw)', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Finish Contractor Verification</h3>
            <p style={{ color: '#374151' }}>You need to complete identity/business verification before submitting a bid.</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button className="btn btn-secondary" onClick={() => setShowVerificationRequired(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={() => { setShowVerificationRequired(false); onNavigate('/contractor-dashboard?tab=verification'); }}>Start Verification</button>
            </div>
          </div>
        </div>
      )}
      {/* CB:END verification-required-modal */}

      {/* CB:BEGIN bid-fee-confirm-modal */}
      {showFeeConfirm && (
        <Elements stripe={stripePromise} options={{ appearance: { theme: 'stripe' } }}>
          <BidFeePaymentModal 
            onClose={() => setShowFeeConfirm(false)}
            onSuccess={async () => {
              const payload = pendingBidRef.current;
              if (!payload) return;
              setSubmitting(true);
              try {
                const clerkId = user?.id as string;
                const { getApiUrl } = await import('../lib/getApiUrl');
                // Use currentProjectId defined at component level
                const amountCents = Math.round(Number(payload.amount) * 100);
                // CB:BEGIN get-payment-intent-id-from-modal
                // Get paymentIntentId from window storage (set by payment modal)
                const paymentIntentId = (window as any).__lastPaymentIntentId || (pendingBidRef.current as any)?.paymentIntentId;
                if (!paymentIntentId) {
                  alert('Payment confirmation not found. Please try submitting your bid again.');
                  setSubmitting(false);
                  return;
                }
                // CB:END get-payment-intent-id-from-modal
                
                const resp = await authFetch(getApiUrl('/api/bids/submit'), {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    projectId: currentProjectId || projectId,
                    clerkId,
                    amountCents,
                    paymentIntentId: paymentIntentId,
                    earliestStartDate: payload.start,
                    estimatedCompletionDate: payload.end,
                    note: (pendingBidRef.current as any)?.note ?? notes ?? undefined,
                  }),
                });
                if (!resp.ok) {
                  let message = await resp.text();
                  let code: string | undefined;
                  try {
                    const jsonError = JSON.parse(message);
                    message = jsonError.message || jsonError.error || message;
                    code = jsonError.code;
                  } catch {}
                  if (resp.status === 403 && code === 'AGREEMENT_REQUIRED') {
                    const goToVerification = window.confirm(
                      'You must accept the Contractor Platform Agreement before submitting bids. Complete verification (step 5) to accept it. Go to verification now?'
                    );
                    if (goToVerification) onNavigate('/contractor-verification');
                    throw new Error('Contractor Platform Agreement must be accepted before bidding.');
                  }
                  throw new Error(message || `Bid submit failed (${resp.status})`);
                }
                alert('Bid submitted!');
                onNavigate('/contractor-dashboard?tab=active');
              } catch (e: any) {
                alert(e?.message ?? 'Failed to submit bid');
              } finally {
                setSubmitting(false);
                setShowFeeConfirm(false);
              }
            }}
            clerkId={user?.id as string}
            projectId={currentProjectId || projectId}
            pendingBidRef={pendingBidRef}
          />
        </Elements>
      )}
      {/* CB:END bid-fee-confirm-modal */}
    </div>
  );
};

// CB:BEGIN bid-fee-payment-modal-component
// Payment modal component for bid fee
const BidFeePaymentModal: React.FC<{
  onClose: () => void;
  onSuccess: () => void;
  clerkId: string;
  projectId?: string | null; // CB:BEGIN pass-projectid-to-modal - Pass projectId to modal
  pendingBidRef?: React.MutableRefObject<any>; // CB:BEGIN pass-pendingbidref-to-modal - Pass pendingBidRef to modal
}> = (props) => {
  const { onClose, onSuccess, clerkId, projectId } = props;
  // CB:BEGIN ensure-pendingbidref-in-scope - Always define pendingBidRef in scope, even if undefined
  // Extract pendingBidRef from props with a default to ensure it's always defined in scope
  const pendingBidRef: React.MutableRefObject<any> | undefined = props.pendingBidRef;
  // CB:END ensure-pendingbidref-in-scope
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // CB:BEGIN fetch-bid-fee-from-backend
  const [bidFeeInfo, setBidFeeInfo] = useState<{ amountCents: number; formatted: string } | null>(null);
  const [loadingFee, setLoadingFee] = useState(true);
  
  // Fetch bid fee amount from backend (source of truth)
  React.useEffect(() => {
    const fetchBidFee = async () => {
      try {
        const { getApiUrl } = await import('../lib/getApiUrl');
        const res = await authFetch(getApiUrl('/api/payments/bid-fee-info'));
        if (res.ok) {
          const data = await res.json();
          setBidFeeInfo({ amountCents: data.amountCents, formatted: data.formatted });
        } else {
          // Fallback to $5.00 if API fails
          setBidFeeInfo({ amountCents: 500, formatted: '$5.00' });
        }
      } catch (e) {
        console.error('Failed to fetch bid fee:', e);
        // Fallback to $5.00 if API fails
        setBidFeeInfo({ amountCents: 500, formatted: '$5.00' });
      } finally {
        setLoadingFee(false);
      }
    };
    fetchBidFee();
  }, []);
  // CB:END fetch-bid-fee-from-backend

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('💳 BidFeePaymentModal: handleSubmit called', { 
      hasStripe: !!stripe, 
      hasElements: !!elements,
      stripeKeyConfigured: !!import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY
    });
    
    if (!stripe || !elements) {
      const errorMsg = !stripe ? 'Stripe not loaded - check VITE_STRIPE_PUBLISHABLE_KEY' : 'Stripe Elements not loaded';
      console.error('❌', errorMsg);
      setError(errorMsg);
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        console.error('❌ Card element not found');
        setError('Card element not found');
        setIsProcessing(false);
        return;
      }

      console.log('💳 Creating payment method from card element...');
      
      // Create payment method
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (pmError || !paymentMethod) {
        console.error('❌ Failed to create payment method:', pmError);
        setError(pmError?.message || 'Failed to create payment method');
        setIsProcessing(false);
        return;
      }

      console.log('✅ Payment method created:', paymentMethod.id, 'Last 4:', paymentMethod.card?.last4);

      // Dev mode validation - only allow test card 4242
      const isDevMode = window.location.hostname === 'dev.corebids.com' || 
                        window.location.hostname === 'localhost' ||
                        import.meta.env.MODE === 'development';
      
      if (isDevMode && paymentMethod.card && paymentMethod.card.last4 !== '4242') {
        setError('In development mode, only the test card 4242 4242 4242 4242 is accepted.');
        setIsProcessing(false);
        return;
      }

      // Charge the bid fee
      const { getApiUrl } = await import('../lib/getApiUrl');
      
      // Get projectId - prioritize prop, fallback to pendingBidRef if safely available
      // CB:BEGIN defensive-pendingbidref-check - Safely access pendingBidRef with defensive check
      let currentProjectIdForFee: string | undefined = projectId || undefined;
      
      // Only try to access pendingBidRef if projectId prop is not available
      if (!currentProjectIdForFee && pendingBidRef) {
        try {
          // Check if pendingBidRef has the expected structure
          if (typeof pendingBidRef === 'object' && pendingBidRef !== null && 'current' in pendingBidRef) {
            const refValue = pendingBidRef.current as any;
            if (refValue && typeof refValue === 'object' && 'projectId' in refValue) {
              currentProjectIdForFee = refValue.projectId;
            }
          }
        } catch (err) {
          // If pendingBidRef is not accessible, just use projectId prop (already set above)
          console.warn('[BidFeePaymentModal] Could not access pendingBidRef, using projectId prop only:', err);
        }
      }
      // CB:END defensive-pendingbidref-check
      
      console.log('💳 Charging bid fee with payment method:', { 
        paymentMethodId: paymentMethod.id, 
        projectId: currentProjectIdForFee 
      });
      
      // CB:BEGIN remove-amount-from-request
      // Backend ignores amountCents from frontend for security - always uses BID_FEE_CENTS env var
      const feeRes = await authFetch(getApiUrl('/api/payments/contractor-bid-fee'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clerkId,
          // amountCents removed - backend uses BID_FEE_CENTS env variable
          paymentMethodId: paymentMethod.id,
          projectId: currentProjectIdForFee // Include projectId so backend can find existing PaymentIntent
        })
      });
      // CB:END remove-amount-from-request

      const feeData = await feeRes.json();
      
      console.log('💳 Backend response:', {
        ok: feeRes.ok,
        status: feeRes.status,
        charged: feeData.charged,
        paymentStatus: feeData.status,
        paymentIntentId: feeData.paymentIntentId,
        hasClientSecret: !!feeData.clientSecret,
        requiresAction: feeData.requiresAction,
        error: feeData.error
      });

      if (!feeRes.ok) {
        const errorMsg = feeData.error || feeData.details || 'Bid fee charge failed';
        console.error('❌ Backend error:', errorMsg, feeData);
        setError(errorMsg);
        setIsProcessing(false);
        return;
      }

      // CB:BEGIN verify-payment-before-success
      // Only proceed if payment is fully confirmed (succeeded)
      // Store paymentIntentId for bid submission verification
      // Also save payment method ID if this is the first time (for future auto-charges)
      if (feeData.charged && feeData.status === 'succeeded' && feeData.paymentIntentId) {
        // Payment succeeded - store paymentIntentId for bid submission
        console.log('✅ Payment succeeded, paymentIntentId:', feeData.paymentIntentId);
        // Store paymentIntentId in window for onSuccess callback to access
        (window as any).__lastPaymentIntentId = feeData.paymentIntentId;
        // If payment method was just saved, next time it will auto-charge
        if (feeData.usedSavedPaymentMethod === false) {
          console.log('✅ Payment method saved - future bids will auto-charge');
        }
        setIsProcessing(false);
        onSuccess();
      } else if (feeData.clientSecret || feeData.requiresAction) {
        // PaymentIntent created but needs confirmation (3DS)
        console.log('💳 Payment requires action, confirming with client secret...');
        const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(feeData.clientSecret);
        if (confirmError) {
          console.error('❌ Payment confirmation error:', confirmError);
          setError(confirmError.message || 'Payment confirmation failed');
          setIsProcessing(false);
          return;
        }
        // Verify payment succeeded
        if (paymentIntent && paymentIntent.status === 'succeeded') {
          // Store paymentIntentId for bid submission
          console.log('✅ Payment confirmed, paymentIntentId:', paymentIntent.id);
          (window as any).__lastPaymentIntentId = paymentIntent.id;
          // Payment method will be saved automatically by backend after successful charge
          setIsProcessing(false);
          onSuccess();
        } else {
          console.error('❌ Payment not confirmed, status:', paymentIntent?.status);
          setError(`Payment not confirmed. Status: ${paymentIntent?.status || 'unknown'}. Please try again.`);
          setIsProcessing(false);
        }
      } else {
        console.error('❌ Unexpected payment response:', feeData);
        setError(String(feeData.error || 'Payment not processed. Please check console for details.'));
        setIsProcessing(false);
      }
      // CB:END verify-payment-before-success
    } catch (e: any) {
      console.error('❌ Unexpected error in handleSubmit:', e);
      const errorMsg = e?.message || 'An unexpected error occurred';
      console.error('❌ Error details:', {
        message: errorMsg,
        stack: e?.stack,
        name: e?.name
      });
      setError(errorMsg + (e?.stack ? `\n\nDetails: ${e.stack.substring(0, 200)}` : ''));
      setIsProcessing(false);
    }
  };
  
  // Log Stripe initialization status
  React.useEffect(() => {
    stripePromise.then(stripe => {
      if (stripe) {
        console.log('✅ Stripe Elements ready');
      } else {
        console.error('❌ Stripe Elements failed to initialize - check VITE_STRIPE_PUBLISHABLE_KEY');
      }
    });
  }, []);

  return (
    <div role="dialog" aria-modal="true" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }} onClick={onClose}>
      <div className="card" style={{ background: 'white', borderRadius: 12, padding: 24, width: 'min(480px, 92vw)', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0, marginBottom: 8 }}>Pay Bid Submission Fee</h3>
        <p style={{ color: '#374151', marginBottom: 20 }}>
          {loadingFee ? 'Loading...' : `A ${bidFeeInfo?.formatted || '$5.00'} fee is required to submit this bid.`}
        </p>
        
        {error && (
          <div style={{ background: '#fee2e2', color: '#dc2626', padding: '12px', borderRadius: '6px', marginBottom: 16, fontSize: '14px' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
              Card Information
            </label>
            <div style={{ border: '1px solid #d1d5db', borderRadius: '6px', padding: '12px', background: 'white' }}>
              {stripe ? (
                <CardElement
                  options={{
                    style: {
                      base: {
                        fontSize: '16px',
                        color: '#424770',
                        '::placeholder': {
                          color: '#aab7c4',
                        },
                      },
                      invalid: {
                        color: '#9e2146',
                      },
                    },
                  }}
                />
              ) : (
                <div style={{ padding: '20px', textAlign: 'center', color: '#dc2626' }}>
                  ❌ Stripe not loaded. Please check VITE_STRIPE_PUBLISHABLE_KEY in environment variables.
                </div>
              )}
            </div>
            {(window.location.hostname === 'dev.corebids.com' || window.location.hostname === 'localhost') && (
              <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
                💡 Use test card: 4242 4242 4242 4242 (any future date, any CVC)
              </p>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button 
              type="button"
              className="btn btn-secondary" 
              onClick={onClose}
              disabled={isProcessing}
            >
              Cancel
            </button>
            <button 
              type="submit"
              className="btn btn-primary" 
              disabled={isProcessing || !stripe}
            >
              {isProcessing ? 'Processing...' : `Pay ${bidFeeInfo?.formatted || '$5.00'} & Submit Bid`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
// CB:END bid-fee-payment-modal-component

export default ProjectDetails; 